// ============================================
// offlineQueue — UKA-X01 D9
//
// IndexedDB-backed verdict queue for offline support.
// DB: 'hemologix-portal', store: 'verdict_queue', version 1.
//
// Verdicts enqueued here when Firestore writes fail offline.
// drainQueue() is called by useOfflineQueue on 'online' event.
// ============================================

// ── Types ─────────────────────────────────────────────────────────────────────

export interface QueuedVerdict {
  queueId: string;
  verdictId: string;
  tenantId: string;
  contentId: string;
  verdictData: Record<string, unknown>;
}

const DB_NAME = 'hemologix-portal';
const STORE_NAME = 'verdict_queue';
const DB_VERSION = 1;

// ── openDB ────────────────────────────────────────────────────────────────────

/**
 * Opens (or creates) the IndexedDB database and object store.
 * Safe to call multiple times — IDB handles upgrade events idempotently.
 */
export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;

    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(new Error(`offlineQueue: Failed to open IndexedDB: ${String(err)}`));
      return;
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'queueId' });
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      const err = (event.target as IDBOpenDBRequest).error;
      reject(
        new Error(
          `offlineQueue: IndexedDB open failed: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };

    request.onblocked = () => {
      reject(
        new Error(
          'offlineQueue: IndexedDB open blocked — another tab has an older version open.',
        ),
      );
    };
  });
}

// ── enqueue ───────────────────────────────────────────────────────────────────

/**
 * Adds a verdict to the queue. Generates a queueId via crypto.randomUUID().
 */
export async function enqueue(
  item: Omit<QueuedVerdict, 'queueId'>,
): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readwrite');
    } catch (err) {
      reject(
        new Error(`offlineQueue: Failed to open transaction for enqueue: ${String(err)}`),
      );
      return;
    }

    const store = tx.objectStore(STORE_NAME);
    const record: QueuedVerdict = {
      ...item,
      queueId: crypto.randomUUID(),
    };

    const req = store.add(record);

    req.onsuccess = () => resolve();
    req.onerror = (event) => {
      const err = (event.target as IDBRequest).error;
      reject(
        new Error(
          `offlineQueue: Failed to enqueue verdict ${record.verdictId}: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };
  });
}

// ── getAll ────────────────────────────────────────────────────────────────────

/**
 * Returns all queued verdicts in insertion order.
 */
export async function getAll(): Promise<QueuedVerdict[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readonly');
    } catch (err) {
      reject(
        new Error(`offlineQueue: Failed to open transaction for getAll: ${String(err)}`),
      );
      return;
    }

    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();

    req.onsuccess = (event) => {
      resolve((event.target as IDBRequest<QueuedVerdict[]>).result ?? []);
    };
    req.onerror = (event) => {
      const err = (event.target as IDBRequest).error;
      reject(
        new Error(
          `offlineQueue: Failed to read verdict queue: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };
  });
}

// ── getQueueDepth ─────────────────────────────────────────────────────────────

/**
 * Returns the count of items currently in the queue.
 */
export async function getQueueDepth(): Promise<number> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readonly');
    } catch (err) {
      reject(
        new Error(
          `offlineQueue: Failed to open transaction for getQueueDepth: ${String(err)}`,
        ),
      );
      return;
    }

    const store = tx.objectStore(STORE_NAME);
    const req = store.count();

    req.onsuccess = (event) => {
      resolve((event.target as IDBRequest<number>).result ?? 0);
    };
    req.onerror = (event) => {
      const err = (event.target as IDBRequest).error;
      reject(
        new Error(
          `offlineQueue: Failed to count verdict queue: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };
  });
}

// ── remove ────────────────────────────────────────────────────────────────────

/**
 * Removes a single item from the queue by queueId.
 * Call after a verdict has been successfully drained to Firestore.
 */
export async function remove(queueId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readwrite');
    } catch (err) {
      reject(
        new Error(
          `offlineQueue: Failed to open transaction for remove: ${String(err)}`,
        ),
      );
      return;
    }

    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(queueId);

    req.onsuccess = () => resolve();
    req.onerror = (event) => {
      const err = (event.target as IDBRequest).error;
      reject(
        new Error(
          `offlineQueue: Failed to remove queueId ${queueId}: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };
  });
}

// ── clearQueue ────────────────────────────────────────────────────────────────

/**
 * Removes ALL items from the queue. Use only for testing or manual admin reset.
 * Normal flows use drainQueue() which removes items after successful Firestore write.
 */
export async function clearQueue(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readwrite');
    } catch (err) {
      reject(
        new Error(`offlineQueue: Failed to open transaction for clearQueue: ${String(err)}`),
      );
      return;
    }

    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();

    req.onsuccess = () => resolve();
    req.onerror = (event) => {
      const err = (event.target as IDBRequest).error;
      reject(
        new Error(
          `offlineQueue: Failed to clear verdict queue: ${err?.message ?? 'unknown error'}`,
        ),
      );
    };
  });
}

// ── drainQueue ────────────────────────────────────────────────────────────────

export interface DrainResult {
  drained: number;
  skipped: number;
  errors: number;
}

/**
 * Iterates all queued items, calls onItem for each.
 * Successfully processed items (return 'success') are removed from the queue.
 * 'skip' items are also removed (already written, idempotent).
 * 'error' items are left in queue for retry.
 *
 * @param onItem Async handler — return 'success', 'skip', or 'error'.
 */
export async function drainQueue(
  onItem: (item: QueuedVerdict) => Promise<'success' | 'skip' | 'error'>,
): Promise<DrainResult> {
  const items = await getAll();

  let drained = 0;
  let skipped = 0;
  let errors = 0;

  for (const item of items) {
    let outcome: 'success' | 'skip' | 'error';

    try {
      outcome = await onItem(item);
    } catch {
      outcome = 'error';
    }

    if (outcome === 'success') {
      try {
        await remove(item.queueId);
      } catch {
        // Removal failure is non-fatal — item will be re-processed next drain.
        // The idempotent check in onItem prevents double-writes.
      }
      drained++;
    } else if (outcome === 'skip') {
      try {
        await remove(item.queueId);
      } catch {
        // Same as above — non-fatal.
      }
      skipped++;
    } else {
      errors++;
    }
  }

  return { drained, skipped, errors };
}
